import { useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Loader2, Sparkles } from "lucide-react";
import { CHANNELS } from "./shared";

const EMPTY = { client_name: "", client_contact: "", channel: "whatsapp", booking_number: "", first_message: "" };
const field = "h-11 rounded-none border-black";

export function NewTranslatorSessionDialog({ open, onOpenChange, onCreated }) {
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.first_message.trim()) return toast.error("Pega el primer mensaje del cliente para detectar su idioma");
    setBusy(true);
    try {
      const { data } = await api.post("/translator/sessions", form);
      setForm(EMPTY);
      onCreated(data);
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-none border-2 border-black max-w-lg" data-testid="translator-new-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-xl">Nueva conversación</DialogTitle>
          <DialogDescription>Detectaremos el idioma del cliente a partir de su primer mensaje.</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <Label className="text-[10px] uppercase tracking-widest">Nombre del cliente *</Label>
              <Input required value={form.client_name} onChange={set("client_name")} className={`${field} mt-1`} data-testid="translator-input-name" />
            </div>
            <div>
              <Label className="text-[10px] uppercase tracking-widest">Teléfono / email</Label>
              <Input value={form.client_contact} onChange={set("client_contact")} className={`${field} mt-1`} data-testid="translator-input-contact" />
            </div>
            <div>
              <Label className="text-[10px] uppercase tracking-widest">Canal</Label>
              <select value={form.channel} onChange={set("channel")} className={`${field} mt-1 w-full border px-3 bg-white text-sm`} data-testid="translator-input-channel">
                {Object.entries(CHANNELS).map(([k, c]) => <option key={k} value={k}>{c.label}</option>)}
              </select>
            </div>
            <div>
              <Label className="text-[10px] uppercase tracking-widest">Nº de reserva (opcional)</Label>
              <Input value={form.booking_number} onChange={set("booking_number")} placeholder="DSG-00001" className={`${field} mt-1`} data-testid="translator-input-booking" />
            </div>
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-widest">Primer mensaje del cliente *</Label>
            <Textarea rows={4} value={form.first_message} onChange={set("first_message")} placeholder="Pega aquí el mensaje tal como te llegó…" className="rounded-none border-black mt-1" data-testid="translator-input-first-message" />
          </div>
          <Button type="submit" disabled={busy} className="w-full h-11 rounded-none bg-black hover:bg-[#E63946]" data-testid="translator-create-submit">
            {busy ? <Loader2 className="animate-spin" size={16} /> : <Sparkles size={16} />}
            <span className="ml-2">{busy ? "Detectando idioma…" : "Crear y traducir"}</span>
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}
